import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientAPI } from '../../../services';
import { useAsyncAction } from '../../../hooks';
import {
  Button,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Badge,
} from '../../UI';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Calendar, 
  Users, 
  DollarSign, 
  Package, 
  Settings,
  CheckCircle,
  XCircle,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';

const ClientFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeSection, setActiveSection] = useState('company');
  
  const { execute: executeCreate } = useAsyncAction();
  const { execute: executeUpdate } = useAsyncAction();

  // Get default trial end date (14 days from now)
  const getDefaultTrialEnd = () => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toISOString().split('T')[0];
  };

  const [form, setForm] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: 'India',
      pincode: ''
    },
    adminUser: {
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
    },
    subscription: {
      type: 'trial',
      plan: 'basic',
      trialStartDate: new Date().toISOString().split('T')[0],
      trialEndDate: getDefaultTrialEnd(),
      employeeLimit: 10,
      perEmployeeCost: 0,
      modules: {
        employees: true,
        customers: true,
        locations: true,
        attendance: true,
        leaves: true,
        payroll: false,
        holidays: true,
        resignation: true,
        talentAcquisition: false,
        expenses: false,
        lnd: false,
        moodCheckin: false,
        pms: false,
        policies: false,
        rewards: false,
        assets: false
      }
    },
    editPermissions: {
      canEditEmployeeSalary: false,
      canEditPayrollDates: false
    },
    workTimings: {
      startTime: '09:00',
      endTime: '18:00',
      workingDays: [1, 2, 3, 4, 5],
      gracePeriodMinutes: 15,
      minimumWorkHours: 8
    }
  });

  useEffect(() => {
    if (isEditMode) {
      fetchClientData();
    }
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getClient(id);
      if (response.status === 'success' && response.data?.client) {
        const client = response.data.client;
        setForm({
          name: client.name || '',
          code: client.code || '',
          email: client.email || '',
          phone: client.phone || '',
          address: client.address || {
            street: '',
            city: '',
            state: '',
            country: 'India',
            pincode: ''
          },
          adminUser: {
            firstName: '',
            lastName: '',
            username: '',
            email: '',
            password: '',
          },
          subscription: {
            type: client.subscription?.type || 'trial',
            plan: client.subscription?.plan || 'basic',
            trialStartDate: client.subscription?.trialStartDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            trialEndDate: client.subscription?.trialEndDate?.split('T')[0] || getDefaultTrialEnd(),
            employeeLimit: client.subscription?.employeeLimit || 10,
            perEmployeeCost: client.subscription?.perEmployeeCost || 0,
            modules: {
              employees: true,
              customers: true,
              locations: true,
              attendance: true,
              leaves: true,
              payroll: false,
              holidays: true,
              resignation: true,
              talentAcquisition: false,
              expenses: false,
              lnd: false,
              moodCheckin: false,
              pms: false,
              policies: false,
              rewards: false,
              assets: false,
              ...(client.subscription?.modules || {})
            }
          },
          editPermissions: {
            canEditEmployeeSalary: client.editPermissions?.canEditEmployeeSalary === true,
            canEditPayrollDates: client.editPermissions?.canEditPayrollDates === true
          },
          workTimings: client.workTimings || {
            startTime: '09:00',
            endTime: '18:00',
            workingDays: [1, 2, 3, 4, 5],
            gracePeriodMinutes: 15,
            minimumWorkHours: 8
          }
        });
      }
    } catch (err) {
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validation
      if (!form.name.trim()) {
        setError('Company name is required');
        setSaving(false);
        return;
      }

      if (!isEditMode) {
        if (!form.adminUser.username.trim()) {
          setError('Admin username is required');
          setSaving(false);
          return;
        }
        if (!form.adminUser.password || form.adminUser.password.length < 6) {
          setError('Admin password must be at least 6 characters');
          setSaving(false);
          return;
        }
      }

      // Generate client code from company name if not provided
      const clientCode = form.code || form.name.replace(/\s+/g, '').substring(0, 6).toUpperCase();

      const clientData = {
        name: form.name,
        code: clientCode,
        email: form.email,
        phone: form.phone,
        address: form.address,
        subscription: form.subscription,
        editPermissions: form.editPermissions,
        workTimings: form.workTimings,
        ...(isEditMode ? {} : { adminUser: form.adminUser })
      };

      let result;
      if (isEditMode) {
        result = await executeUpdate(clientAPI.updateClient, id, clientData);
      } else {
        result = await executeCreate(clientAPI.createClient, clientData);
      }

      if (result.success) {
        setSuccess(isEditMode ? 'Client updated successfully!' : 'Client created successfully!');
        setTimeout(() => {
          navigate('/dashboard/clients');
        }, 1500);
      } else {
        setError(result.error || `Failed to ${isEditMode ? 'update' : 'create'} client`);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (path, value) => {
    setForm(prev => {
      const keys = path.split('.');
      const newForm = { ...prev };
      let current = newForm;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newForm;
    });
  };

  const toggleModule = (module) => {
    setForm(prev => ({
      ...prev,
      subscription: {
        ...prev.subscription,
        modules: {
          ...prev.subscription.modules,
          [module]: !prev.subscription.modules[module]
        }
      }
    }));
  };

  const sections = [
    { id: 'company', label: 'Company Info', icon: Building2 },
    { id: 'admin', label: 'Admin User', icon: User },
    { id: 'subscription', label: 'Subscription', icon: Package },
    { id: 'modules', label: 'Modules', icon: Settings },
    { id: 'edit-controls', label: 'Edit Controls', icon: Lock },
    { id: 'timings', label: 'Work Timings', icon: Calendar }
  ];

  const modules = [
    { key: 'employees', label: 'Employees', description: 'Manage employee profiles and data' },
    { key: 'customers', label: 'Customers', description: 'Customer relationship management' },
    { key: 'locations', label: 'Locations', description: 'Office and branch locations' },
    { key: 'attendance', label: 'Attendance', description: 'Track employee attendance' },
    { key: 'leaves', label: 'Leaves', description: 'Leave management system' },
    { key: 'payroll', label: 'Payroll', description: 'Salary and payroll processing' },
    { key: 'holidays', label: 'Holidays', description: 'Holiday calendar management' },
    { key: 'resignation', label: 'Resignation', description: 'Employee resignation and separation' },
    { key: 'talentAcquisition', label: 'Talent Acquisition', description: 'Recruitment and hiring management' },
    { key: 'expenses', label: 'Expenses', description: 'Employee expense claims and reimbursements' },
    { key: 'lnd', label: 'Learning & Development', description: 'Employee training, courses, assessments and certifications' },
    { key: 'moodCheckin', label: 'Mood Check-In', description: 'Daily employee mood pulse and well-being analytics' },
    { key: 'pms', label: 'Performance Management', description: 'Goal setting, KPI tracking, assessments and appraisals' },
    { key: 'policies', label: 'Policy Acknowledgement', description: 'Upload policies and track employee acknowledgements' },
    { key: 'rewards', label: 'Rewards & Recognition', description: 'Employee awards, points, leaderboard and recognition' },
    { key: 'assets', label: 'Asset Management', description: 'Assign and track company assets to employees' }
  ];

  const weekDays = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  const toggleWorkingDay = (day) => {
    setForm(prev => {
      const days = prev.workTimings.workingDays || [];
      const newDays = days.includes(day) 
        ? days.filter(d => d !== day) 
        : [...days, day].sort();
      return {
        ...prev,
        workTimings: {
          ...prev.workTimings,
          workingDays: newDays
        }
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/clients')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Edit Client' : 'Add New Client'}
                </h1>
                <p className="text-sm text-gray-500">
                  {isEditMode ? 'Update client information' : 'Create a new client organization'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/clients')}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={saving}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isEditMode ? 'Update Client' : 'Create Client'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <ErrorMessage message={error} className="mb-6" />}
        {success && <SuccessMessage message={success} className="mb-6" />}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 p-2 sticky top-24">
              <nav className="space-y-1">
                {sections.map((section) => {
                  // Hide admin section in edit mode
                  if (section.id === 'admin' && isEditMode) return null;
                  
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Company Information */}
              {activeSection === 'company' && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Company Information</h2>
                      <p className="text-sm text-gray-500">Basic details about the organization</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => updateForm('name', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter company name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Code
                      </label>
                      <input
                        type="text"
                        value={form.code}
                        onChange={(e) => updateForm('code', e.target.value.toUpperCase())}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors uppercase"
                        placeholder="Auto-generated if empty"
                        maxLength={10}
                      />
                      <p className="mt-1 text-xs text-gray-500">Used for login (e.g., TECH01)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => updateForm('email', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="company@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => updateForm('phone', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="9542643011"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={form.address?.street || ''}
                        onChange={(e) => updateForm('address.street', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Street address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={form.address?.city || ''}
                        onChange={(e) => updateForm('address.city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <input
                        type="text"
                        value={form.address?.state || ''}
                        onChange={(e) => updateForm('address.state', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="State"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                      <input
                        type="text"
                        value={form.address?.country || 'India'}
                        onChange={(e) => updateForm('address.country', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Country"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                      <input
                        type="text"
                        value={form.address?.pincode || ''}
                        onChange={(e) => updateForm('address.pincode', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Pincode"
                        maxLength={6}
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <Button
                      type="button"
                      onClick={() => setActiveSection(isEditMode ? 'subscription' : 'admin')}
                    >
                      Next: {isEditMode ? 'Subscription' : 'Admin User'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Admin User (only for new clients) */}
              {activeSection === 'admin' && !isEditMode && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Admin User</h2>
                      <p className="text-sm text-gray-500">Create the primary administrator account</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={form.adminUser.firstName}
                        onChange={(e) => updateForm('adminUser.firstName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="First name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={form.adminUser.lastName}
                        onChange={(e) => updateForm('adminUser.lastName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Last name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={form.adminUser.username}
                          onChange={(e) => updateForm('adminUser.username', e.target.value.toLowerCase())}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="admin_username"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={form.adminUser.email}
                          onChange={(e) => updateForm('adminUser.email', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="admin@company.com"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={form.adminUser.password}
                          onChange={(e) => updateForm('adminUser.password', e.target.value)}
                          className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Minimum 6 characters"
                          minLength={6}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Password must be at least 6 characters</p>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setActiveSection('company')}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setActiveSection('subscription')}
                    >
                      Next: Subscription
                    </Button>
                  </div>
                </div>
              )}

              {/* Subscription */}
              {activeSection === 'subscription' && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Package className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Subscription Details</h2>
                      <p className="text-sm text-gray-500">Configure billing and limits</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subscription Type
                      </label>
                      <select
                        value={form.subscription.type}
                        onChange={(e) => updateForm('subscription.type', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="trial">Trial</option>
                        <option value="paid">Paid</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan
                      </label>
                      <select
                        value={form.subscription.plan}
                        onChange={(e) => updateForm('subscription.plan', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      >
                        <option value="basic">Basic</option>
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employee Limit
                      </label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="number"
                          value={form.subscription.employeeLimit}
                          onChange={(e) => updateForm('subscription.employeeLimit', parseInt(e.target.value) || 0)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          min={1}
                        />
                      </div>
                    </div>

                    {form.subscription.type === 'trial' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Trial Start Date
                          </label>
                          <input
                            type="date"
                            value={form.subscription.trialStartDate}
                            onChange={(e) => updateForm('subscription.trialStartDate', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Trial End Date
                          </label>
                          <input
                            type="date"
                            value={form.subscription.trialEndDate}
                            onChange={(e) => updateForm('subscription.trialEndDate', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          />
                        </div>
                      </>
                    )}

                    {form.subscription.type === 'paid' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Per Employee Cost (₹/month)
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="number"
                            value={form.subscription.perEmployeeCost}
                            onChange={(e) => updateForm('subscription.perEmployeeCost', parseInt(e.target.value) || 0)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            min={0}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setActiveSection(isEditMode ? 'company' : 'admin')}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setActiveSection('modules')}
                    >
                      Next: Modules
                    </Button>
                  </div>
                </div>
              )}

              {/* Modules */}
              {activeSection === 'modules' && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                      <Settings className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Module Access</h2>
                      <p className="text-sm text-gray-500">Enable or disable features for this client</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {modules.map((module) => (
                      <div
                        key={module.key}
                        onClick={() => toggleModule(module.key)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          form.subscription.modules[module.key]
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {form.subscription.modules[module.key] ? (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                              <XCircle className="w-6 h-6 text-gray-400" />
                            )}
                            <div>
                              <h3 className="font-medium text-gray-900">{module.label}</h3>
                              <p className="text-sm text-gray-500">{module.description}</p>
                            </div>
                          </div>
                          <div
                            className={`w-12 h-6 rounded-full transition-colors ${
                              form.subscription.modules[module.key] ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div
                              className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5 ${
                                form.subscription.modules[module.key] ? 'translate-x-6' : 'translate-x-0.5'
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setActiveSection('subscription')}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setActiveSection('edit-controls')}
                    >
                      Next: Edit Controls
                    </Button>
                  </div>
                </div>
              )}

              {/* Edit Controls */}
              {activeSection === 'edit-controls' && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Edit Controls</h2>
                      <p className="text-sm text-gray-500">Choose which sensitive admin edits require super admin approval first</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div
                      onClick={() => updateForm('editPermissions.canEditEmployeeSalary', !form.editPermissions.canEditEmployeeSalary)}
                      className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${
                        form.editPermissions.canEditEmployeeSalary
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-gray-900">Allow Employee Salary Edits</h3>
                          <p className="mt-1 text-sm text-gray-500">When off, admins can view salary but cannot change employee CTC or salary structure.</p>
                        </div>
                        {form.editPermissions.canEditEmployeeSalary ? (
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-6 h-6 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    <div
                      onClick={() => updateForm('editPermissions.canEditPayrollDates', !form.editPermissions.canEditPayrollDates)}
                      className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${
                        form.editPermissions.canEditPayrollDates
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-gray-900">Allow Payroll Date Edits</h3>
                          <p className="mt-1 text-sm text-gray-500">When off, admins cannot change pay period start and end dates in payroll settings.</p>
                        </div>
                        {form.editPermissions.canEditPayrollDates ? (
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-6 h-6 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setActiveSection('modules')}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setActiveSection('timings')}
                    >
                      Next: Work Timings
                    </Button>
                  </div>
                </div>
              )}

              {/* Work Timings */}
              {activeSection === 'timings' && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Work Timings</h2>
                      <p className="text-sm text-gray-500">Configure default work schedule</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={form.workTimings.startTime}
                        onChange={(e) => updateForm('workTimings.startTime', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={form.workTimings.endTime}
                        onChange={(e) => updateForm('workTimings.endTime', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grace Period (minutes)
                      </label>
                      <input
                        type="number"
                        value={form.workTimings.gracePeriodMinutes}
                        onChange={(e) => updateForm('workTimings.gracePeriodMinutes', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        min={0}
                        max={60}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Work Hours
                      </label>
                      <input
                        type="number"
                        value={form.workTimings.minimumWorkHours}
                        onChange={(e) => updateForm('workTimings.minimumWorkHours', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        min={1}
                        max={24}
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Working Days
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {weekDays.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleWorkingDay(day.value)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            form.workTimings.workingDays?.includes(day.value)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 flex justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setActiveSection('edit-controls')}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      loading={saving}
                      className="flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {isEditMode ? 'Update Client' : 'Create Client'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientFormPage;
